import { IconPlus } from '@tabler/icons-react';
import { useState, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from 'contexts/AuthContext';
import { NSButton, NSTable } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import useToast from 'hooks/useToast';
import { useGetTimelines } from './hooks/useGetTimeline';
import { useCreateTimeline } from './hooks/useCreateTimeline';
import { useDuplicateTimeline } from './hooks/useDuplicateTimeline';
import { useDeleteTimeline } from './hooks/useDeleteTimeline';
import './Timeline.scss';
import 'react-datepicker/dist/react-datepicker.css';
import TimelineActivityRow from './components/TimelineActivityRow/TimelineActivityRow';
import { TimelineSlideout } from './components/TimelineSlideout/TimelineSlideout';
import { useSelectedActivity } from './hooks/useSelectedActivity';
import { TimelineItem } from './types';
import { DETAILS_TAB, ATTACHMENTS_TAB } from 'views/DealDashboard/constants';

const NEW_ACTIVITY_NAME = 'Untitled activity';

const Timeline = ({ dealId }: { dealId: number }) => {
    const { selectedOrganizationId } = useAuthContext();
    const queryClient = useQueryClient();
    const { showError } = useToast();

    const listRef = useRef<HTMLDivElement>(null);
    const [editMode, setEditMode] = useState(false);
    const [pendingDeleteTimeline, setPendingDeleteTimeline] = useState<TimelineItem | null>(null);

    const { data: timelines, isLoading: isLoadingTimelines } = useGetTimelines({
        orgId: selectedOrganizationId!, dealId, includeAssignees: true,
    });

    const timelineData: TimelineItem[] = useMemo(() => timelines ?? [], [timelines]);

    const {
        selectedTimelineId, selectedTimeline, activityTab, openActivity, closeActivity, setActivityTab,
    } = useSelectedActivity(timelineData, isLoadingTimelines);

    const openSlideout = (id: string, tab?: typeof DETAILS_TAB | typeof ATTACHMENTS_TAB) => {
        openActivity(id, tab);
        setEditMode(false);
    };

    const closeSlideout = () => {
        closeActivity();
        setEditMode(false);
    };

    const { mutate: createTimeline, isPending: isCreatingTimeline } = useCreateTimeline();
    const { mutate: duplicateTimeline } = useDuplicateTimeline();
    const { mutate: deleteTimeline } = useDeleteTimeline();

    const invalidateTimelines = () => queryClient.invalidateQueries({
        queryKey: ['timelines', selectedOrganizationId!, dealId],
    });

    const handleAddNewRow = () => {
        createTimeline(
            { orgId: selectedOrganizationId!, dealId, postData: { name: NEW_ACTIVITY_NAME } },
            {
                onSuccess: (created: TimelineItem) => {
                    invalidateTimelines();
                    openActivity(created.id);
                    setEditMode(true);
                },
                onError: () => showError('Failed to create activity'),
            },
        );
    };

    const handleDuplicate = (timeline: TimelineItem) => {
        duplicateTimeline(
            { orgId: selectedOrganizationId!, dealId, timelineId: timeline.id },
            {
                onSuccess: (newTimeline) => {
                    // Optimistically append to every cached timelines query variant so the
                    // slideout can resolve the new id before the refetch lands.
                    queryClient.setQueriesData<TimelineItem[]>(
                        { queryKey: ['timelines', selectedOrganizationId!, dealId] },
                        old => (old ? [...old, newTimeline] : [newTimeline]),
                    );
                    invalidateTimelines();
                    openSlideout(newTimeline.id);
                },
                onError: () => showError('Failed to duplicate activity'),
            },
        );
    };

    const handleRequestDelete = (timeline: TimelineItem) => setPendingDeleteTimeline(timeline);

    const handleConfirmDelete = () => {
        if (!pendingDeleteTimeline) return;
        const deletedId = pendingDeleteTimeline.id;
        deleteTimeline(
            { orgId: selectedOrganizationId!, dealId, timelineId: deletedId },
            {
                onSuccess: () => {
                    invalidateTimelines();
                    setPendingDeleteTimeline(null);
                    if (selectedTimelineId === deletedId) {
                        closeSlideout();
                    }
                },
                onError: () => showError('Failed to delete activity'),
            },
        );
    };

    return (
        <div className="px-2 pb-2">
            <div ref={listRef} className="TimelineTable__container">
                <NSTable className="TimelineTable" hover>
                    <colgroup>
                        <col />
                        <col className="TimelineTable__col-target" />
                        <col className="TimelineTable__col-completed" />
                        <col className="TimelineTable__col-assignees" />
                        <col />
                        <col className="TimelineTable__col-attachments" />
                        <col className="TimelineTable__col-actions" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className="d-flex justify-content-between align-items-center">
                                <span>Activity</span>
                                <NSButton
                                    className="TimelineTable__add-btn font-weight-bold cursor--pointer heap-deal-dashboard-overview-add-activity"
                                    callback={handleAddNewRow}
                                    ariaLabel="Add activity"
                                    iconOnly
                                    icon={IconPlus}
                                    color="link"
                                    disabled={isCreatingTimeline}
                                />
                            </th>
                            <th>Target</th>
                            <th>Date completed</th>
                            <th>Assignees</th>
                            <th>Status</th>
                            <th />
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <RenderIf isTrue={timelineData?.length > 0} isLoading={isLoadingTimelines}>
                            {timelineData?.map(item => (
                                <TimelineActivityRow
                                    key={item.id}
                                    item={item}
                                    onRowClick={(id) => openSlideout(id)}
                                    onAttachmentClick={(id) => openSlideout(id, ATTACHMENTS_TAB)}
                                    onDuplicate={handleDuplicate}
                                    onRequestDelete={handleRequestDelete}
                                />
                            ))}
                        </RenderIf>
                    </tbody>
                </NSTable>
            </div>
            <TimelineSlideout
                isOpen={selectedTimelineId !== null}
                timeline={selectedTimeline}
                activityTab={activityTab}
                onTabChange={setActivityTab}
                dealId={dealId}
                orgId={selectedOrganizationId!}
                editMode={editMode}
                setEditMode={setEditMode}
                attachmentsCount={selectedTimeline?.attachmentCount ?? 0}
                onClose={closeSlideout}
                onSwitchTimeline={openSlideout}
                onDuplicate={handleDuplicate}
                onRequestDelete={handleRequestDelete}
            />
            <ConfirmationModal
                isOpen={pendingDeleteTimeline !== null}
                danger
                toggle={() => setPendingDeleteTimeline(null)}
                onCancel={() => setPendingDeleteTimeline(null)}
                onConfirm={handleConfirmDelete}
                modalHeaderText={`Delete "${pendingDeleteTimeline?.name ?? ''}"?`}
                modalFooterButtonText="Delete"
                modalFooterButtonColor="danger"
            >
                <p>Are you sure you want to delete this activity? This action cannot be undone.</p>
            </ConfirmationModal>
        </div>
    );
};

export default Timeline;
